// src/components/configuration/sections/BusinessSettingsShadcn.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';

// shadcn components
import { Alert, AlertDescription } from '../../ui/alert';
import { Spinner } from '../../ui/spinner';

// Keep your existing PageContainer for headers
import PageContainer from '../../ui/layout/PageContainer/PageContainer';

// Import the shadcn split panel components
import BusinessSettingsListShadcn from '../../ui/sections/BusinessSettingsList/BusinessSettingsListShadcn';
import BusinessSettingsEditorShadcn from '../../ui/sections/BusinessSettingsEditor/BusinessSettingsEditorShadcn';

// Keep your existing hook - same data pipeline!
import { useSettings } from '../../../hooks/useSettings';

// Helper functions for day conversion - UNCHANGED
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const convertNumberKeysToNames = (hoursData) => {
  if (!hoursData) return {};

  const converted = {};
  Object.keys(hoursData).forEach(key => {
    const dayNumber = parseInt(key);
    if (dayNumber >= 0 && dayNumber <= 6) {
      const dayName = DAY_NAMES[dayNumber];
      const hourData = hoursData[key];

      if (hourData === null) {
        // Day is closed
        converted[dayName] = {
          is_open: false,
          open_time: '09:00',
          close_time: '17:00'
        };
      } else {
        // Day has hours - convert field names
        converted[dayName] = {
          is_open: true,
          open_time: hourData.open,   // Convert 'open' to 'open_time'
          close_time: hourData.close  // Convert 'close' to 'close_time'
        };
      }
    }
  });
  return converted;
};

const convertNameKeysToNumbers = (hoursData) => {
  if (!hoursData) return {};

  const converted = {};
  Object.keys(hoursData).forEach(dayName => {
    const dayNumber = DAY_NAMES.indexOf(dayName);
    if (dayNumber !== -1) {
      const dayData = hoursData[dayName];

      if (!dayData.is_open) {
        // Day is closed
        converted[dayNumber] = null;
      } else {
        // Day has hours - convert field names back
        converted[dayNumber] = {
          open: dayData.open_time,   // Convert 'open_time' to 'open'
          close: dayData.close_time  // Convert 'close_time' to 'close'
        };
      }
    }
  });
  return converted;
};

const BusinessSettingsShadcn = () => {
  // Settings context - UPDATED with email methods and domain management
  const {
    businessSettings,
    businessHours,
    updateSetting,
    updateSettings,
    updateBusinessHours,
    loading,
    savingSettings,
    savingHours,
    error,
    getSetting,
    getBusinessHoursForDay,
    notificationPreferences,
    updateNotificationPreference,
    savingNotificationPrefs,
    getNotificationPreference,
    isNotificationEnabled,
    getNotificationRecipients,
    // EMAIL METHODS:
    emailSettings,
    updateEmailSettings,
    setupProfessionalDomain,
    checkDomainVerification,
    savingEmailSettings,
    checkingDomainVerification,
    getEmailSetting,
    // DOMAIN MANAGEMENT:
    emailDomains,
    setActiveEmailDomain,
    setBasicEmailMode,
    getEmailDomains,
    getActiveEmailDomain
  } = useSettings();

  // URL query params for section navigation
  const [searchParams, setSearchParams] = useSearchParams();

  // Navigation state - NEW
  const [selectedSection, setSelectedSection] = useState('company');

  // Handle ?section= query param on mount
  useEffect(() => {
    const sectionParam = searchParams.get('section');
    if (sectionParam) {
      // Valid sections
      const validSections = ['company', 'hours', 'operational', 'email', 'notifications', 'payments', 'integrations'];
      if (validSections.includes(sectionParam)) {
        setSelectedSection(sectionParam);
        // Clean up URL after handling
        searchParams.delete('section');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  // Local form state - UNCHANGED
  const [companyForm, setCompanyForm] = useState({
    company_name: '',
    company_address: '',
    company_city: '',
    company_state: 'TX',
    company_zip: '',
    company_phone: '',
    company_email: '',
    company_website: ''
  });

  const [operationalForm, setOperationalForm] = useState({
    min_notice_hours: 3,
    travel_buffer: 30,
    max_concurrent_appointments: 1,
    allow_overlapping_bookings: false,
    tax_enabled: false,
    tax_rate: 8.25,
    default_payment_terms: 'Due on receipt'
  });

  const [hoursForm, setHoursForm] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  // UPDATED EMAIL FORM STATE - NEW STRUCTURE
  const [emailForm, setEmailForm] = useState({
    // Basic config
    reply_to_email: '',
    business_signature: '',
    configuration_type: 'basic', // 'basic' or 'professional'

    // Professional mode fields
    selectedDomainId: null,      // Which existing domain to use
    custom_domain: '',           // For creating new domain
    sending_email: '',           // For creating new domain
    showAddDomainForm: false     // Whether add domain form is visible
  });

  // ADD ORIGINAL DATA TRACKING FOR CHANGE DETECTION
  const [originalCompanyForm, setOriginalCompanyForm] = useState({});
  const [originalOperationalForm, setOriginalOperationalForm] = useState({});
  const [originalHoursForm, setOriginalHoursForm] = useState({});
  const [originalEmailForm, setOriginalEmailForm] = useState({});

  // Domain collision detection helper
  const isDomainNameTaken = (domainName) => {
    const domains = getEmailDomains();
    return domains.some(domain => domain.domain.toLowerCase() === domainName.toLowerCase());
  };

  // Initialize forms with existing data - UPDATED to store originals
  useEffect(() => {
    if (businessSettings) {
      const companyData = {
        company_name: getSetting('company_name', ''),
        company_address: getSetting('company_address', ''),
        company_city: getSetting('company_city', ''),
        company_state: getSetting('company_state', 'TX'),
        company_zip: getSetting('company_zip', ''),
        company_phone: getSetting('company_phone', ''),
        company_email: getSetting('company_email', ''),
        company_website: getSetting('company_website', '')
      };

      const operationalData = {
        min_notice_hours: getSetting('min_notice_hours', 3),
        travel_buffer: getSetting('travel_buffer', 30),
        max_concurrent_appointments: getSetting('max_concurrent_appointments', 1),
        allow_overlapping_bookings: getSetting('allow_overlapping_bookings', false),
        tax_enabled: getSetting('tax_enabled', false),
        tax_rate: getSetting('tax_rate', 8.25),
        default_payment_terms: getSetting('default_payment_terms', 'Due on receipt')
      };

      setCompanyForm(companyData);
      setOperationalForm(operationalData);

      // STORE ORIGINALS FOR CHANGE DETECTION
      setOriginalCompanyForm(companyData);
      setOriginalOperationalForm(operationalData);
    }
  }, [businessSettings, getSetting]);

  useEffect(() => {
    if (businessHours) {
      // Convert numeric keys to day names for the form
      const convertedHours = convertNumberKeysToNames(businessHours);
      setHoursForm(convertedHours);
      // ADD ORIGINAL TRACKING:
      setOriginalHoursForm(convertedHours);
    }
  }, [businessHours]);

  // UPDATED EMAIL FORM INITIALIZATION - NEW STRUCTURE
  useEffect(() => {
    if (emailSettings && emailDomains) {
      const activeDomain = getActiveEmailDomain();

      const emailData = {
        reply_to_email: getEmailSetting('reply_to_email', ''),
        business_signature: getEmailSetting('business_signature', ''),
        configuration_type: getEmailSetting('configuration_type', 'basic'),
        selectedDomainId: activeDomain ? activeDomain.id : null,
        custom_domain: '',
        sending_email: '',
        showAddDomainForm: false
      };

      setEmailForm(emailData);
      setOriginalEmailForm(emailData);
    }
  }, [emailSettings, emailDomains, getEmailSetting, getActiveEmailDomain]);

  // Form handlers - UPDATED with correct method names
  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    await updateSettings(companyForm);
    setSuccessMessage('Company information updated successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleOperationalSubmit = async (e) => {
    e.preventDefault();
    await updateSettings(operationalForm);
    setSuccessMessage('Operational settings updated successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleHoursSubmit = async (e) => {
    e.preventDefault();
    // Convert day names back to numbers for the backend
    const convertedHours = convertNameKeysToNumbers(hoursForm);
    await updateBusinessHours(convertedHours);
    setSuccessMessage('Business hours updated successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleHourChange = (day, field, value) => {
    setHoursForm(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  // UPDATED EMAIL FORM HANDLER - SMART ROUTING LOGIC
  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    try {
      // Always save basic email configuration
      const basicSettings = {
        reply_to_email: emailForm.reply_to_email,
        business_signature: emailForm.business_signature
      };

      // Smart routing based on configuration type and form state
      if (emailForm.configuration_type === 'basic') {
        // Switch to basic mode
        await setBasicEmailMode();
        await updateEmailSettings(basicSettings);
        setSuccessMessage('Switched to basic email mode successfully!');
      } else {
        // Professional mode
        if (emailForm.custom_domain && emailForm.sending_email) {
          // Create new domain workflow
          if (isDomainNameTaken(emailForm.custom_domain)) {
            setSuccessMessage('Domain already exists - switched to existing domain.');
            const existingDomain = getEmailDomains().find(d =>
              d.domain.toLowerCase() === emailForm.custom_domain.toLowerCase()
            );
            await setActiveEmailDomain(existingDomain.id);
          } else {
            // Create new domain
            await setupProfessionalDomain(emailForm.custom_domain, emailForm.sending_email);
            setSuccessMessage(`Professional domain setup initiated for ${emailForm.custom_domain}`);
          }
        } else if (emailForm.selectedDomainId) {
          // Switch to existing domain
          const currentActive = getActiveEmailDomain();
          if (!currentActive || currentActive.id !== emailForm.selectedDomainId) {
            await setActiveEmailDomain(emailForm.selectedDomainId);
            setSuccessMessage('Active domain changed successfully!');
          }
        }

        // Update basic settings
        await updateEmailSettings(basicSettings);
      }

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Email form save error:', error);
    }
  };

  // CHANGE DETECTION FUNCTIONS
  const hasCompanyChanges = Object.keys(companyForm).some(key => {
    const currentValue = companyForm[key];
    const originalValue = originalCompanyForm[key];
    return currentValue !== (originalValue || '');
  });

  const hasOperationalChanges = Object.keys(operationalForm).some(key => {
    const currentValue = operationalForm[key];
    const originalValue = originalOperationalForm[key];

    if (typeof currentValue === 'boolean') {
      return currentValue !== (originalValue || false);
    }
    if (typeof currentValue === 'number') {
      return currentValue !== (originalValue || 0);
    }
    return currentValue !== (originalValue || '');
  });

  const hasHoursChanges = Object.keys(hoursForm).some(dayKey => {
    const currentDay = hoursForm[dayKey];
    const originalDay = originalHoursForm[dayKey];

    if (!currentDay && !originalDay) return false;
    if (!currentDay || !originalDay) return true;

    return (
      currentDay.is_open !== originalDay.is_open ||
      currentDay.open_time !== originalDay.open_time ||
      currentDay.close_time !== originalDay.close_time
    );
  });

  // UPDATED EMAIL CHANGE DETECTION
  const hasEmailChanges = Object.keys(emailForm).some(key => {
    const currentValue = emailForm[key];
    const originalValue = originalEmailForm[key];

    if (typeof currentValue === 'boolean') {
      return currentValue !== (originalValue || false);
    }
    return currentValue !== (originalValue || '');
  });

  // Check completion status - UNCHANGED
  const isCompanyInfoComplete = companyForm.company_name && companyForm.company_phone;
  const isHoursConfigured = Object.keys(hoursForm).some(day =>
    hoursForm[day] && hoursForm[day].is_open
  );

  // Check if notifications are configured - UNCHANGED
  const isNotificationConfigured = notificationPreferences && notificationPreferences.length > 0 &&
    notificationPreferences.some(pref => pref.enabled && pref.settings?.recipients?.length > 0);

  // Options data - UNCHANGED
  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  const stateOptions = [
    { value: 'TX', label: 'Texas' },
    { value: 'CA', label: 'California' },
    { value: 'NY', label: 'New York' },
    { value: 'FL', label: 'Florida' }
  ];

  const paymentTermsOptions = [
    { value: 'Due on receipt', label: 'Due on receipt' },
    { value: 'Net 15 days', label: 'Net 15 days' },
    { value: 'Net 30 days', label: 'Net 30 days' },
    { value: 'Net 60 days', label: 'Net 60 days' }
  ];

  // Loading state - UNCHANGED
  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Spinner className="size-16 text-primary mx-auto mb-4" />
            <p className="text-foreground text-lg">Loading business settings...</p>
            <p className="text-muted-foreground text-sm mt-2">Retrieving your configuration</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer heightMode="viewport">
      {/* Success Message */}
      {successMessage && (
        <Alert className="mb-4 flex-shrink-0 bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
          <AlertDescription>
            {successMessage}
          </AlertDescription>
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert variant="destructive" className="mb-4 flex-shrink-0">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Main Content - Split Panel Layout */}
      <div className="grid grid-cols-12 gap-4 h-full">
        {/* Left Panel - Settings List (33% width) */}
        <div className="col-span-4 h-full min-h-0">
          <BusinessSettingsListShadcn
            selectedSection={selectedSection}
            onSelectSection={setSelectedSection}
            completionStatus={{
              company: isCompanyInfoComplete,
              hours: isHoursConfigured,
              operational: true,
              email: true,
              notifications: isNotificationConfigured
            }}
          />
        </div>

        {/* Right Panel - Settings Editor (67% width) */}
        <div className="col-span-8 h-full min-h-0">
          <BusinessSettingsEditorShadcn
            selectedSection={selectedSection}
            // Pass all the form data and handlers
            companyForm={companyForm}
            setCompanyForm={setCompanyForm}
            handleCompanySubmit={handleCompanySubmit}
            operationalForm={operationalForm}
            setOperationalForm={setOperationalForm}
            handleOperationalSubmit={handleOperationalSubmit}
            hoursForm={hoursForm}
            handleHoursSubmit={handleHoursSubmit}
            handleHourChange={handleHourChange}
            // EMAIL FORM PROPS - UPDATED:
            emailForm={emailForm}
            setEmailForm={setEmailForm}
            handleEmailSubmit={handleEmailSubmit}
            // Pass all the state flags
            savingSettings={savingSettings}
            savingHours={savingHours}
            // EMAIL SAVING STATE:
            savingEmailSettings={savingEmailSettings}
            // CHANGE DETECTION FLAGS:
            hasCompanyChanges={hasCompanyChanges}
            hasOperationalChanges={hasOperationalChanges}
            hasHoursChanges={hasHoursChanges}
            hasEmailChanges={hasEmailChanges}
            // Pass utility data
            daysOfWeek={daysOfWeek}
            stateOptions={stateOptions}
            paymentTermsOptions={paymentTermsOptions}
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default BusinessSettingsShadcn;